import type { CostServiceErrorCode } from './errors.js';

export type CostServiceHttpStatus = 400 | 404 | 409;

/**
 * HTTP status per Cost Services error code (ALC-4474). One table for both transports: the API
 * filter answers with it and the MCP mapper reads it to tell a missing resource (404) from a
 * refused request, so neither side keeps a copy that could drift. 404 when the resource in the
 * path is gone; 409 when the request is valid but the state of the service refuses it (activation
 * preconditions, a status that does not allow the action, a name or a window that is already
 * taken); 400 when the request itself names something invalid (a malformed knob value, a vendor,
 * account or billing service that does not exist). Exhaustive: a new code does not compile until
 * it has a status.
 */
export const COST_SERVICE_ERROR_HTTP_STATUS: Readonly<
  Record<CostServiceErrorCode, CostServiceHttpStatus>
> = Object.freeze({
  COST_SERVICE_KNOB_UNKNOWN: 400,
  COST_SERVICE_KNOB_VALUE_INVALID: 400,
  COST_SERVICE_TOLERANCE_INVALID: 400,
  COST_SERVICE_VENDOR_REQUIRED: 409,
  COST_SERVICE_RATE_REQUIRED: 409,
  COST_SERVICE_COST_ACCOUNT_REQUIRED: 409,
  COST_SERVICE_LIABILITY_ACCOUNT_REQUIRED: 409,
  COST_SERVICE_VARIANCE_ACCOUNT_REQUIRED: 409,
  COST_SERVICE_LIABILITY_ACCOUNT_IS_AP_CONTROL: 409,
  // A window is checked on the request that sets it (PATCH, attach) and again at activation.
  COST_SERVICE_ACTIVE_RANGE_INVALID: 400,
  COST_SERVICE_TEMPLATE_UNKNOWN: 400,
  COST_SERVICE_NOT_FOUND: 404,
  COST_SERVICE_ENTITY_NOT_FOUND: 400,
  COST_SERVICE_NAME_TAKEN: 409,
  COST_SERVICE_STATUS_INVALID: 409,
  COST_SERVICE_VENDOR_NOT_FOUND: 400,
  COST_SERVICE_ACCOUNT_INVALID: 400,
  COST_SERVICE_BILLING_SERVICE_NOT_FOUND: 400,
  COST_SERVICE_ATTACHMENT_NOT_FOUND: 404,
  COST_SERVICE_ATTACHMENT_OVERLAP: 409,
  // A valid request that the live links of the service refuse (the base type is locked).
  COST_SERVICE_BASE_TYPE_LOCKED: 409,
  COST_SERVICE_ATTACHMENT_CHANGE_REQUIRED: 400,
  COST_SERVICE_RATE_OVERRIDE_REQUIRED_TO_REOPEN: 400,
  COST_SERVICE_RATE_EFFECTIVE_FROM_REQUIRED: 400,
  // D24 guard / D22 schedule: valid requests that posted history refuses.
  COST_SERVICE_RATE_EFFECTIVE_FROM_BEFORE_POSTED: 409,
  COST_SERVICE_RATE_EFFECTIVE_FROM_IN_CLOSED_PERIOD: 409,
  COST_SERVICE_FIXED_RECOGNITION_LOCKED: 409,
  COST_SERVICE_FIXED_SCHEDULE_NOT_FOR_MONTHLY: 400,
  COST_SERVICE_RENEWAL_CHARGE_NOT_FOR_BASE: 400,
  COST_SERVICE_RATE_LOCKED_USE_LINK_CHANGE: 409,
  COST_ENTRY_NOT_FOUND: 404,
  COST_ENTRY_FILTER_INVALID: 400,
  COST_RECOGNITION_SOURCE_JOURNAL_NOT_FOUND: 409,
  COST_RECOGNITION_INVOICE_LINE_ITEM_MISSING: 409,
  // ALC-4476 — the approval checks refuse a selection that the current balances do not allow.
  COST_LINE_PROVISION_WITHOUT_ENTRIES: 409,
  COST_MATCH_EXCEEDS_BILL_LINE: 400,
  COST_MATCH_EXCEEDS_ENTRY_BALANCE: 409,
  COST_MATCH_VENDOR_MISMATCH: 400,
  COST_ENTRY_NOT_OPEN: 409,
  COST_ENTRY_SCHEDULED_NOT_DUE: 409,
  COST_MATCH_VARIANCE_OUT_OF_TOLERANCE: 409,
  COST_LINE_DESTINATION_NOT_AVAILABLE: 400,
  COST_MATCH_NOT_FOUND: 404,
  COST_MATCH_UNDO_REASON_REQUIRED: 400,
  AP_BILL_NOT_EDITABLE: 409,
  AP_BILL_GENERATED_LINE_HEADER_LOCKED: 409,
  AP_BILL_LAST_LINE_ITEM: 409,
  AP_BILL_LINE_ITEM_HAS_HISTORY: 409,
  // D45f: replaced a plain 400 on every line mutation; kept a 400 so callers see no new status.
  AP_BILL_LINE_ITEMS_LOCKED: 400,
  COST_LINE_NOT_FOUND: 404,
  COST_MATCH_CURRENCY_MISMATCH: 400,
  COST_MATCH_SELECTION_INVALID: 400,
  COST_MATCH_SET_NOT_FOUND: 409,
  // ALC-4476 checkpoint B — approval posting, later match, ADR 0016.
  COST_LINE_CLASSIFICATION_REQUIRED: 409,
  COST_LINE_SHARED_CLASSIFICATION_MULTIPLE: 409,
  COST_LINE_SHARED_CLASSIFICATION_SUM_MISMATCH: 409,
  COST_LINE_POSTING_INCOMPATIBLE: 409,
  COST_MATCH_ACCOUNT_NOT_MAPPED: 409,
  COST_MATCH_BILL_NOT_POSTED: 409,
  // ALC-4686: a valid request the approved bill's state refuses on this channel (match on the bill screen).
  COST_MATCH_AFTER_APPROVAL_UNAVAILABLE: 409,
  COST_MATCH_PERIOD_NOT_OPEN: 409,
  COST_PREPAID_RECLASS_NO_OPEN_PERIOD: 409,
  COST_MATCH_LINE_SETTLED_BY_ACCRUAL: 409,
  COST_REVERSAL_PERIOD_NOT_OPEN: 409,
  COST_REVERSAL_ACCOUNT_NOT_MAPPED: 409,
  // ALC-4477 — close-out.
  COST_PROVISION_CLOSE_REASON_REQUIRED: 400,
  COST_PROVISION_CLOSE_PERIOD_NOT_OPEN: 409,
  COST_PROVISION_CLOSE_RESERVED_BY_BILL: 409,
  COST_PROVISION_CLOSE_ACCOUNT_NOT_MAPPED: 409,
  COST_MATCH_RELIEF_REDUCED_BY_REVERSAL: 409,
  COST_REVERSAL_RETRY_BILL_RELIEF_CHANGED: 409,
  COST_MATCH_RELIEF_BEFORE_RECOGNITION: 409,
  COST_SERVICE_PREPAID_ACCOUNT_NOT_CONFIGURED: 409,
  COST_AWAITING_SELECTION_INVALID: 400,
  COST_AWAITING_RESERVATION_NOT_FOUND: 404,
  COST_AWAITING_RESERVATION_NOT_OPEN: 409,
  COST_AWAITING_MONTH_ALREADY_PROVISIONED: 409,
  COST_AWAITING_MONTH_WILL_NOT_POST: 409,
  COST_LINE_SERVICE_CHOICE_NOT_AVAILABLE: 400,
  COST_LINE_SPLIT_INVALID: 400,
  COST_LINE_SPLIT_SUM_MISMATCH: 400,
  COST_LINE_FIFO_NO_COST_SERVICE: 409,
  COST_LINE_FIFO_EXCESS_NO_MONTH: 409,
  // D45 alerts: warnings, never thrown (the map is exhaustive, so they still need a status).
  COST_LINE_FIFO_SERVICE_INACTIVE_OPEN_PROVISIONS: 409,
  COST_LINE_FIFO_EXCESS_TO_EXPENSE: 409,
  COST_LINE_FIFO_EXCESS_TO_VARIANCE: 409,
  COST_LINE_FIFO_VARIANCE_ACCOUNT_NOT_CONFIGURED: 409,
  COST_LINE_SERVICE_INACTIVE: 400,
  COST_LINE_SERVICE_OTHER_VENDOR: 400,
  COST_LINE_SPLIT_PART_EXCEEDED: 400,
  COST_LINE_SERVICE_CHOICE_MISMATCH: 400,
});
